/**
 * The comms channel: one voice, and every line it can say.
 *
 * RIG CONTROL, RADAR and SONAR are all the same actor, because what the player is
 * listening to is a channel rather than a cast — one operator having a bad week, reporting
 * from whichever station is talking.
 *
 * Everything here is baked to static audio at build time by `tools/bakeRadio.ts`. The game
 * is zero-cost and fully client-side, so the running page can never hold an API key or
 * make a paid call; it fetches an mp3 whose name it derives from the line itself. That is
 * why every speakable line has to be *enumerable* — see `radioRegistry.ts`, and the test
 * that fails the build if any line lacks a take.
 *
 * The rule that falls out of it: **dynamic text is never spoken.** A line assembled at
 * runtime (a cargo summary, a vessel's name) cannot have been baked, so the voice takes
 * the fixed part and the variable detail stays on the HUD where it can be read.
 */

import type { ShiftScript } from './schema';

/** Adam's casting. Both are part of the take's identity — see `lineHash`. */
export const VOICE_ID = 'hNqLXjknMk756717MabO';
export const VOICE_MODEL = 'eleven_v3';

/** Where the baked takes and the two channel clicks live, relative to the site root. */
export const COMMS_DIR = '/audio/comms';

export const CLICK_IN_URL = `${COMMS_DIR}/comms-click-in.wav`;
export const CLICK_OUT_URL = `${COMMS_DIR}/comms-click-out.wav`;

/**
 * A stable content hash, identical in Node and in the browser.
 *
 * The take's filename *is* its identity: change the words or the direction and you get a
 * different file, which is exactly the re-record. Leave them alone and the bake tool skips
 * the line and bills nothing. Two independent FNV-1a passes, so ~100 lines have no
 * realistic chance of colliding.
 */
export function lineHash(read: string): string {
  const key = `${VOICE_ID}|${VOICE_MODEL}|${read}`;
  let a = 0x811c9dc5;
  let b = 0x01000193;
  for (let i = 0; i < key.length; i++) {
    const c = key.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b + c + i, 0x85ebca6b) >>> 0;
  }
  return (a.toString(36) + b.toString(36)).padStart(12, '0');
}

export function commsUrl(read: string): string {
  return `${COMMS_DIR}/${lineHash(read)}.mp3`;
}

/**
 * The Kraken's own narration, keyed by phase.
 *
 * Moved out of `sim/world.ts` so the bake can see it: the finale's lines are the ones that
 * most need a performance, and they were the easiest to forget precisely because they are
 * not script beats.
 */
export const KRAKEN_LINES: Partial<Record<string, { text: string; read: string }>> = {
  /*
   * The reveal. Adam wanted the spotter to lose his professionalism here — "my god,
   * look at the size of that thing" — rather than report a contact, and he is right:
   * this is the first time anyone on the rig has seen the whole animal, and the previous
   * line ("It is up. That is the whole thing") narrated it like a radar return. The
   * instruction stays on the end, because this is also where the player has to learn
   * that the gun is not the answer.
   */
  mantle: {
    text: 'My God. Look at the size of that thing. Put a fish into it.',
    read: '[awe] My God. [pause] Look at the size of that thing. [urgent] Put a fish into it!',
  },
  submerged: {
    text: 'It has gone under again. Watch the hydrophone.',
    read: '[tense] It has gone under again. [quietly] Watch the hydrophone.',
  },
  tentacles: {
    text: 'It is under us. Limbs on the platform — cut them off us.',
    read: '[urgent] It is under us. [shouting] Limbs on the platform — cut them off us!',
  },
  rise: {
    text: 'It is looking at us.',
    read: '[whispers] It is looking at us.',
  },
  /*
   * No line for `dying` — deliberately. The eye rolls back and the mass goes under in
   * silence; the spotter does not find a voice until the sea has closed. The old line
   * ("It is breaking up. Keep firing.") belonged to a death by sustained fire, and the
   * death is a single decision now.
   */
  gone: {
    text: 'It is gone. The sea is quiet.',
    read: '[exhales] [quietly] It is gone. [pause] The sea is quiet.',
  },
};

/**
 * Lines the simulation speaks that are not script beats.
 *
 * The vessel-lost report is the reason the "dynamic text is never spoken" rule exists: it
 * used to interpolate a cargo summary into the sentence, which no bake could ever cover.
 * The voice now says the fixed half and the HUD keeps the detail.
 */
export const SYSTEM_LINES = {
  standDown: {
    text: 'That is the watch. Stand down.',
    read: '[relieved] That is the watch. [pause] Stand down.',
  },
  /*
   * The same sign-off cannot serve both endings.
   *
   * A shift that has just lost the hull it existed to protect was closing on "[relieved]
   * That is the watch" — the relief of a clean watch, over a wreck. Worse, it is the only
   * other line in the game with the word *watch* in it, and shift 1 opens on "you have
   * the watch": losing Ardent and then hearing the watch mentioned again reads as the
   * shift restarting, which it is not.
   */
  standDownLost: {
    text: 'That is the watch. We did not bring them all in.',
    read: '[heavy] That is the watch. [pause] [quietly] We did not bring them all in.',
  },
  tankerLost: {
    text: 'Tanker lost.',
    read: '[flat] [defeated] Tanker lost. [pause] That contract goes down with her.',
  },
  supplyLost: {
    text: 'Supply vessel lost.',
    read: '[defeated] Supply vessel lost. [sighs] Whatever she was carrying is on the bottom.',
  },
} as const;

/**
 * Every take a given shift can play, for preloading at shift start.
 *
 * Per shift rather than up front because the whole campaign's speech is several megabytes
 * and none of it is wanted until the shift that says it begins. The system and Kraken
 * lines ride along on every shift: there are eight of them, they are tiny, and any of
 * them can fire in any shift.
 */
export function commsUrlsForScript(script: ShiftScript): string[] {
  const urls = new Set<string>();
  for (const beat of script.beats) {
    if (beat.kind !== 'radio') continue;
    urls.add(commsUrl(beat.read ?? beat.text));
  }
  for (const line of Object.values(SYSTEM_LINES)) urls.add(commsUrl(line.read));
  for (const line of Object.values(KRAKEN_LINES)) {
    if (line) urls.add(commsUrl(line.read));
  }
  return [...urls];
}

/** Endless's fixed line set. A generated watch may only ever say one of these. */
export const ENDLESS_LINES = {
  earlyWatch: {
    text: 'Another watch. Same water.',
    read: '[bored] [tired] Another watch. [pause] Same water.',
  },
  lateWatch: {
    text: 'They know the platform is here now. Nothing about tonight will be gentle.',
    read: '[grim] They know the platform is here now. [pause] [quietly] Nothing about tonight will be gentle.',
  },
  probe: {
    text: 'Contacts close in, port bow. They did not wait for the convoy.',
    read: '[urgent] Contacts close in, port bow. They did not wait for the convoy.',
  },
} as const;
